import Backoff from 'backo';
import { push } from 'connected-react-router';
import {
  all,
  call,
  delay,
  put,
  race,
  take,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';
import { oc } from 'ts-optchain';
import { EnvironmentApi, ProductApi } from 'vrlcm-gui-api';
import {
  EnvironmentRequest,
  EnvironmentState,
  RequestState
} from 'vrlcm-gui-types';
import { downloadFile } from 'vrlcm-gui-utils';
import { requestDatacenters } from '../actions/Datacenters';
import {
  DownloadEnvironmentLogsRequest,
  ExportConfiguration,
  exportConfigurationSuccess,
  ExportProductConfiguration,
  exportProductConfigurationSuccess,
  pollEnvironments,
  pollEnvironmentsFailed,
  PollEnvironmentsStart,
  PollEnvironmentsStartOpts,
  pollEnvironmentsSuccess,
  ToggleHealth,
  TriggerEnvironmentInventorySync,
  TriggerProductInventorySync,
  UpdateEnvironment,
  updateEnvironmentSuccess
} from '../actions/Environments';
import EnvironmentsAPI from '../api/Environments';
import * as Constants from '../constants/action-constants';
import { ROUTES } from '../constants/RouteConstants';
import { EnvironmentItems } from '../types/Environments';

function* getEnvironments(opts?: PollEnvironmentsStartOpts) {
  const backoff = new Backoff({ min: 5000, max: 60000 });
  let count = oc(opts).count();
  const fetchMapData = oc(opts).fetchMapData();
  while (typeof count === 'undefined' || count-- > 0) {
    try {
      yield put(requestDatacenters(false));
      if (!opts?.skipWaitForDatacenters) {
        yield take(Constants.REQUEST_DATACENTERS_SUCCESS);
      }
      yield put(pollEnvironments());
      const isCloudProxyEnvironment =
        opts?.isCloudProxyEnvironment ||
        window.location.pathname?.includes('cloud/');
      const environments: EnvironmentState[] = isCloudProxyEnvironment
        ? yield call(EnvironmentApi.getAllCloudEnvironmentRequests)
        : yield call(EnvironmentsAPI.getEnvironments);
      const completed = environments.filter(
        env => env.requestState === RequestState.COMPLETED
      );
      const pending = environments.filter(
        env =>
          env.requestState !== RequestState.COMPLETED &&
          env.requestState !== RequestState.SUBMITTED
      );
      const items: EnvironmentItems = { completed, pending };
      yield put(pollEnvironmentsSuccess(items, fetchMapData));
      backoff.reset();
      yield delay(15000);
    } catch (error) {
      yield put(pollEnvironmentsFailed());
      yield delay(backoff.duration());
    }
  }
}

function* pollForEnvironments() {
  yield takeEvery(
    Constants.POLL_ENVIRONMENTS_START,
    function* pollEnvironmentsStart(action: PollEnvironmentsStart) {
      yield race({
        poll: call(getEnvironments, action.payload.opts),
        stop: take(Constants.POLL_ENVIRONMENTS_STOP)
      });
    }
  );
}

function* downloadLogRequest(action: DownloadEnvironmentLogsRequest) {
  try {
    const requestId = yield call(
      EnvironmentsAPI.downloadEnvironmentLogs,
      action.payload.environmentId
    );
    yield put(push(`${ROUTES.REQUESTS}/${requestId}`));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForDownloadLogsRequest() {
  yield takeLatest(
    Constants.DOWNLOAD_ENVIRONMENT_LOGS_REQUEST,
    downloadLogRequest
  );
}

function* triggerInventorySync() {
  try {
    yield call(EnvironmentApi.triggerAllEnvironmentsInventorySync);
    yield put(push(ROUTES.REQUESTS));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForTriggerInventorySync() {
  yield takeLatest(Constants.TRIGGER_INVENTORY_SYNC, triggerInventorySync);
}

function* triggerEnvironmentInventorySync(
  action: TriggerEnvironmentInventorySync
) {
  try {
    const { environmentId } = action.payload;
    yield call(EnvironmentsAPI.triggerEnvironmentInventorySync, environmentId);
    yield put(push(ROUTES.REQUESTS));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForTriggerEnvironmentInventorySync() {
  yield takeLatest(
    Constants.TRIGGER_ENVIRONMENT_INVENTORY_SYNC,
    triggerEnvironmentInventorySync
  );
}

function* triggerProductInventorySync(action: TriggerProductInventorySync) {
  try {
    const { environmentId, productId, tenantId } = action.payload;
    const requestId = yield call(
      ProductApi.triggerProductInventorySync,
      environmentId,
      productId,
      tenantId
    );
    yield put(push(`${ROUTES.REQUESTS}/${requestId}`));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForTriggerProductInventorySync() {
  yield takeLatest(
    Constants.TRIGGER_PRODUCT_INVENTORY_SYNC,
    triggerProductInventorySync
  );
}

function* exportConfiguartion(action: ExportConfiguration) {
  try {
    const { environmentId, type } = action.payload;
    const configuration: EnvironmentRequest = yield call(
      EnvironmentsAPI.exportConfiguration,
      environmentId,
      type
    );
    const data = JSON.stringify(configuration, null, 2);
    const file = new Blob([data], { type: 'application/json' });
    const fileName = `${configuration.environmentName}.json`;
    downloadFile(file, fileName);
    yield put(exportConfigurationSuccess(environmentId));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForExportConfiguration() {
  yield takeLatest(Constants.EXPORT_CONFIGURATION, exportConfiguartion);
}

function* exportProductConfiguartion(action: ExportProductConfiguration) {
  try {
    const { environmentId, productId, type, tenantId } = action.payload;
    const configuration: EnvironmentRequest = yield call(
      EnvironmentsAPI.exportProductConfiguration,
      environmentId,
      productId,
      type,
      tenantId
    );
    const data = JSON.stringify(configuration, null, 2);
    const file = new Blob([data], { type: 'application/json' });
    const fileName = `${configuration.environmentName}-${productId}.json`;
    downloadFile(file, fileName);
    yield put(exportProductConfigurationSuccess(environmentId, productId));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForExportProductConfiguration() {
  yield takeLatest(
    Constants.EXPORT_PRODUCT_CONFIGURATION,
    exportProductConfiguartion
  );
}

function* toggleHeath(action: ToggleHealth) {
  try {
    const { environmentId, status } = action.payload;
    const requestId = yield call(
      EnvironmentsAPI.toggleHeatlh,
      environmentId,
      status
    );
    yield put(push(`${ROUTES.REQUESTS}/${requestId}`));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForToggleHealth() {
  yield takeLatest(Constants.TOGGLE_HEALTH, toggleHeath);
}

function* updateEnvironment(action: UpdateEnvironment) {
  try {
    const { environmentId } = action.payload;
    const environment = yield call(
      EnvironmentApi.getEnvironmentRequestById,
      environmentId
    );
    yield put(updateEnvironmentSuccess(environment));
  } catch (error) {
    // @TODO: Handle error
  }
}

function* watchForUpdateEnvironment() {
  yield takeLatest(Constants.UPDATE_ENVIRONMENT, updateEnvironment);
}

export default function* environmentsSaga() {
  yield all([
    pollForEnvironments(),
    watchForDownloadLogsRequest(),
    watchForTriggerInventorySync(),
    watchForTriggerEnvironmentInventorySync(),
    watchForTriggerProductInventorySync(),
    watchForExportConfiguration(),
    watchForExportProductConfiguration(),
    watchForToggleHealth(),
    watchForUpdateEnvironment()
  ]);
}
